import React, { useEffect, useState, useMemo } from 'react';
import {
  Box,
  Button,
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  FormControlLabel,
  FormGroup,
  IconButton,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { useTranslation } from 'react-i18next';
import { Controller, FormProvider, UseFormReturn } from 'react-hook-form';
import { ChangeEvent } from 'react';
import {
  AgentDto,
  ToolDto,
  useToolListControllerAgentTollListQuery,
} from '@shared/services/apiService/apiService';
import { ToolTypeEnum } from '@shared/constants/campaign.constants';
import {
  AGENT_IDLE_STATE_DELAY_MS_MAX,
  AGENT_IDLE_STATE_DELAY_MS_MIN,
  createDefaultIdleStateDraft,
  CUSTOMER_IDLE_STATE_MAX_ATTEMPTS_MAX,
  CUSTOMER_IDLE_STATE_MAX_ATTEMPTS_MIN,
  CUSTOMER_IDLE_STATE_TIMEOUT_SEC_MAX,
  CUSTOMER_IDLE_STATE_TIMEOUT_SEC_MIN,
  ExpandedAgentIdleState,
  ExpandedAgentIdleStateDraft,
  IdleStateValidationErrors,
  mapIdleStateToDraft,
} from '@features/Admin/Agent/model/idleState';
import { AgentToolsFormValue } from '@features/Admin/Agent/model/types';
import { BorderedGrid } from '@shared/components/borderedGrid';
import { MessageTemplateField } from '@shared/components/messageTemplateField';
import { convertLegacyTemplate } from '@shared/utils/messageTemplate';
import { MessageGroupEditor } from '@shared/components/MessageGroupEditor/MessageGroupEditor';
import { PhoneNumbersField } from '@shared/components/PhoneNumbersField';
import { StyledErrorText } from '@shared/components/errorText';
import CustomSwitch from '@shared/components/forms/theme-elements/CustomSwitch';
import CustomTextField from '@shared/components/forms/theme-elements/CustomTextField';
import {
  IconPhoneIncoming,
  IconPhoneOff,
  IconPhoneCall,
  IconMicrophone,
} from '@tabler/icons-react';
import { IconPostCall, IconWhatsapp } from '@shared/assets/images/svgs';
import AgentMcpToolsSection from './AgentMcpToolsSection';
import { GenderSettings } from '@shared/services/apiService/missing-types';
interface AgentAdvancedSettingsModalProps {
  open: boolean;
  onClose: () => void;
  agent: AgentDto;
  agents: AgentDto[];
  campaignTools?: ToolDto[];
  campaignName?: string;
  companyName?: string;
  formMethods: UseFormReturn<AgentToolsFormValue>;
  onSave: (idleState: ExpandedAgentIdleState | null) => Promise<boolean>;
  refetch: () => void;
  isReadOnly?: boolean;
}

type SettingsBlockKey = 'idle' | 'customerIdle' | 'whatsapp' | 'transfer' | 'gender';

type ExpandedSettingsState = Record<SettingsBlockKey, boolean>;

const createExpandedSettingsState = (enabled: boolean): ExpandedSettingsState => ({
  idle: enabled,
  customerIdle: enabled,
  whatsapp: enabled,
  transfer: enabled,
  gender: enabled,
});

const isCustomerIdleConfigured = (draft: ExpandedAgentIdleStateDraft): boolean =>
  [
    draft.customerReplyTimeoutSec,
    draft.customerSilenceThresholdSec,
    draft.customerMaxAttempts,
  ].some((value) => value.trim() !== '');

interface ExpandableSettingsBlockProps {
  id: SettingsBlockKey;
  expanded: boolean;
  onChange: (expanded: boolean) => void;
  header: React.ReactNode;
  children: React.ReactNode;
}

const ExpandableSettingsBlock: React.FC<ExpandableSettingsBlockProps> = ({
  id,
  expanded,
  onChange,
  header,
  children,
}) => (
  <BorderedGrid sx={{ padding: 0, overflow: 'hidden' }}>
    <Accordion
      disableGutters
      elevation={0}
      expanded={expanded}
      onChange={(event, nextExpanded) => {
        const target = event.target as HTMLElement;

        if (target.closest('input, [role="switch"]')) {
          return;
        }

        onChange(nextExpanded);
      }}
      sx={{ '&:before': { display: 'none' } }}>
      <AccordionSummary
        expandIcon={<ExpandMoreIcon />}
        aria-controls={`${id}-settings-content`}
        id={`${id}-settings-header`}
        sx={{
          minHeight: 72,
          px: 2.5,
          '& .MuiAccordionSummary-content': { mr: 1.5 },
        }}>
        {header}
      </AccordionSummary>
      <AccordionDetails id={`${id}-settings-content`} sx={{ px: 2.5, pb: 2.5, pt: 0 }}>
        {children}
      </AccordionDetails>
    </Accordion>
  </BorderedGrid>
);

interface StaticSettingsBlockProps {
  header: React.ReactNode;
  children?: React.ReactNode;
}

const StaticSettingsBlock: React.FC<StaticSettingsBlockProps> = ({ header, children }) => (
  <BorderedGrid sx={{ padding: 2.5, overflow: 'hidden' }}>
    {header}
    {children}
  </BorderedGrid>
);

const AgentAdvancedSettingsModal: React.FC<AgentAdvancedSettingsModalProps> = ({
  open,
  onClose,
  agent,
  agents,
  campaignTools = [],
  campaignName = '',
  companyName = '',
  formMethods,
  onSave,
  refetch,
  isReadOnly = false,
}) => {
  const { t } = useTranslation();
  const theme = useTheme();

  const [idleStateDraft, setIdleStateDraft] = useState<ExpandedAgentIdleStateDraft>(
    createDefaultIdleStateDraft(),
  );
  const [idleStateErrors, setIdleStateErrors] = useState<IdleStateValidationErrors>({});
  const [expandedSettings, setExpandedSettings] = useState<ExpandedSettingsState>(
    createExpandedSettingsState(false),
  );

  const setSettingsExpanded = (key: SettingsBlockKey, expanded: boolean) => {
    setExpandedSettings((previous) => ({ ...previous, [key]: expanded }));
  };

  const {
    data: fetchedAgentTools,
    isLoading: isLoadingAgentTools,
    refetch: refetchAgentTools,
  } = useToolListControllerAgentTollListQuery(
    { id: agent.id || '' },
    { skip: !open || !agent.id, refetchOnMountOrArgChange: true },
  );

  const agentNonMcpTools = useMemo(() => {
    const tools = fetchedAgentTools || (agent.tools as ToolDto[]) || [];
    return tools.filter((tool) => tool.type !== ToolTypeEnum.Mcp);
  }, [fetchedAgentTools, agent.tools]);

  const allAgentTools = useMemo(() => {
    if (fetchedAgentTools) {
      return fetchedAgentTools;
    }
    return campaignTools;
  }, [fetchedAgentTools, campaignTools]);

  const {
    control,
    reset,
    setValue,
    trigger,
    formState: { errors },
    watch,
  } = formMethods;

  const formData = watch();
  // Initialize form with existing agent tools
  useEffect(() => {
    if (open && agent.id && !isLoadingAgentTools) {
      const callbackRequestTool = agentNonMcpTools.find(
        (tool) => tool.type === ToolTypeEnum.RequestCallback,
      );
      const voiceMailDetectionTool = agentNonMcpTools.find(
        (tool) => tool.type === ToolTypeEnum.VoiceMailDetection,
      );
      const endCallTool = agentNonMcpTools.find((tool) => tool.type === ToolTypeEnum.EndCall);
      const sendFollowUpMessageTool = agentNonMcpTools.find(
        (tool) => tool.type === ToolTypeEnum.WhatsappMessageSend,
      );
      const generateWhatsappMessageTool = agentNonMcpTools.find(
        (tool) => tool.type === ToolTypeEnum.GenerateWhatsappMessage,
      );
      const transferTool = agentNonMcpTools.find((tool) => tool.type === ToolTypeEnum.TransferCall);

      const initialMessageTemplate = convertLegacyTemplate(
        sendFollowUpMessageTool?.messageTemplate || '',
      );

      const callHandoffSignalTool = agentNonMcpTools.find(
        (tool) => tool.type === ToolTypeEnum.CallHandoffSignal,
      );
      const genderDetectionTool = agentNonMcpTools.find(
        (tool) => tool.type === ToolTypeEnum.GenderDetection,
      );
      const nextIdleStateDraft = mapIdleStateToDraft(agent.idleState as ExpandedAgentIdleState);
      setExpandedSettings({
        idle: nextIdleStateDraft.enabled,
        customerIdle: isCustomerIdleConfigured(nextIdleStateDraft),
        whatsapp: Boolean(sendFollowUpMessageTool),
        transfer: Boolean(transferTool),
        gender: Boolean(genderDetectionTool),
      });

      reset({
        agentId: agent.id || '',
        callbackRequestTool: callbackRequestTool
          ? { ...callbackRequestTool, enabled: true }
          : { enabled: false, isAsync: true },
        voiceMailDetectionTool: voiceMailDetectionTool
          ? { ...voiceMailDetectionTool, enabled: true }
          : { enabled: false, isAsync: false },
        endCallTool: endCallTool
          ? { ...endCallTool, enabled: true }
          : { enabled: false, isAsync: false },
        sendFollowUpMessageTool: sendFollowUpMessageTool
          ? { ...sendFollowUpMessageTool, enabled: true, messageTemplate: initialMessageTemplate }
          : { enabled: false, messageTemplate: '', isAsync: true },
        generateWhatsappMessageTool: generateWhatsappMessageTool
          ? { ...generateWhatsappMessageTool, enabled: true }
          : { enabled: false, isAsync: true },
        TransferTool: transferTool
          ? {
              ...transferTool,
              enabled: true,
              phoneNumbers: (transferTool as any).phoneNumbers || [],
            }
          : { enabled: false, phoneNumbers: [], isAsync: true },
        callHandoffSignalTool: callHandoffSignalTool
          ? { ...callHandoffSignalTool, enabled: true }
          : { enabled: false },
        genderDetectionTool: genderDetectionTool
          ? {
              ...genderDetectionTool,
              enabled: true,
              confidenceThreshold: (genderDetectionTool.genderSettings as GenderSettings)
                ?.confidenceThreshold,
              durationThreshold: (genderDetectionTool.genderSettings as GenderSettings)
                ?.durationThreshold,
              behaviorTemplate: (genderDetectionTool.genderSettings as GenderSettings)
                ?.behaviorTemplate,
            }
          : { enabled: false },
      });
      setIdleStateDraft(nextIdleStateDraft);
      setIdleStateErrors({});
    }
  }, [open, agent.id, agentNonMcpTools, reset, isLoadingAgentTools]);

  const updateIdleStateDraft = (patch: Partial<ExpandedAgentIdleStateDraft>) => {
    setIdleStateDraft((prev) => ({ ...prev, ...patch }));
    setIdleStateErrors((prev) => {
      const nextErrors = { ...prev };
      Object.keys(patch).forEach((key) => {
        delete nextErrors[key as keyof IdleStateValidationErrors];
      });
      return nextErrors;
    });
  };

  const getOptionalNumberError = (value: number, min: number, max: number, integerOnly = false) => {
    const isValidNumber = integerOnly ? Number.isInteger(value) : Number.isFinite(value);

    if (!isValidNumber || value < min || value > max) {
      return t('adminMainPage.agent.idleState.errors.optionalRange', { min, max });
    }

    return null;
  };

  const validateIdleState = (): ExpandedAgentIdleState | null => {
    const nextErrors: IdleStateValidationErrors = {};
    const delayMs = Number(idleStateDraft.delayMs);
    const customerReplyTimeoutSec = idleStateDraft.customerReplyTimeoutSec.trim()
      ? Number(idleStateDraft.customerReplyTimeoutSec)
      : null;
    const customerSilenceThresholdSec = idleStateDraft.customerSilenceThresholdSec.trim()
      ? Number(idleStateDraft.customerSilenceThresholdSec)
      : null;
    const customerMaxAttempts = idleStateDraft.customerMaxAttempts.trim()
      ? Number(idleStateDraft.customerMaxAttempts)
      : null;
    const messages = idleStateDraft.messages.map((message) => message.trim()).filter(Boolean);
    const customerSilenceMessages = idleStateDraft.customerSilenceMessages
      .map((message) => message.trim())
      .filter(Boolean);
    const customerEndCallMessages = idleStateDraft.customerEndCallMessages
      .map((message) => message.trim())
      .filter(Boolean);

    if (idleStateDraft.enabled) {
      if (
        !Number.isInteger(delayMs) ||
        delayMs < AGENT_IDLE_STATE_DELAY_MS_MIN ||
        delayMs > AGENT_IDLE_STATE_DELAY_MS_MAX
      ) {
        nextErrors.delayMs = t('adminMainPage.agent.idleState.errors.range', {
          min: AGENT_IDLE_STATE_DELAY_MS_MIN,
          max: AGENT_IDLE_STATE_DELAY_MS_MAX,
        });
      }

      if (!messages.length) {
        nextErrors.messages = t('adminMainPage.agent.idleState.errors.messagesRequired');
      }
    }

    if (customerReplyTimeoutSec !== null) {
      const error = getOptionalNumberError(
        customerReplyTimeoutSec,
        CUSTOMER_IDLE_STATE_TIMEOUT_SEC_MIN,
        CUSTOMER_IDLE_STATE_TIMEOUT_SEC_MAX,
      );

      if (error) {
        nextErrors.customerReplyTimeoutSec = error;
      }
    }

    if (customerSilenceThresholdSec !== null) {
      const error = getOptionalNumberError(
        customerSilenceThresholdSec,
        CUSTOMER_IDLE_STATE_TIMEOUT_SEC_MIN,
        CUSTOMER_IDLE_STATE_TIMEOUT_SEC_MAX,
      );

      if (error) {
        nextErrors.customerSilenceThresholdSec = error;
      }
    }

    if (customerMaxAttempts !== null) {
      const error = getOptionalNumberError(
        customerMaxAttempts,
        CUSTOMER_IDLE_STATE_MAX_ATTEMPTS_MIN,
        CUSTOMER_IDLE_STATE_MAX_ATTEMPTS_MAX,
        true,
      );

      if (error) {
        nextErrors.customerMaxAttempts = error;
      }
    }

    if (
      customerReplyTimeoutSec !== null &&
      customerSilenceThresholdSec !== null &&
      customerSilenceThresholdSec >= customerReplyTimeoutSec
    ) {
      nextErrors.customerSilenceThresholdSec = t(
        'adminMainPage.agent.idleState.errors.silenceThresholdRelation',
      );
    }

    setIdleStateErrors(nextErrors);

    if (Object.keys(nextErrors).length > 0) {
      return null;
    }

    return {
      enabled: idleStateDraft.enabled,
      delayMs: idleStateDraft.enabled ? delayMs : AGENT_IDLE_STATE_DELAY_MS_MIN,
      messages,
      customerSilenceMessages,
      customerEndCallMessages,
      customerReplyTimeoutSec,
      customerSilenceThresholdSec,
      customerMaxAttempts,
    };
  };

  const handleSave = async () => {
    if (isReadOnly) return;

    const isValid = await trigger();
    const validatedIdleState = validateIdleState();
    const errorFields = Object.keys(errors);
    const hasValidationErrors = !isValid || errorFields.length > 0 || !validatedIdleState;
    if (hasValidationErrors) {
      return;
    }

    await onSave(validatedIdleState);
  };

  const templatePreviewValues = {
    agentName: agent.name || '',
    campaignName,
    companyName,
  };

  // console.log('formData', formData); // noisy: uncomment only when debugging

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        <Stack direction="row" alignItems="center" justifyContent="space-between">
          <Typography variant="h6">
            {t('adminMainPage.operation.advancedSettings')} - {agent.name}
          </Typography>
          <IconButton onClick={onClose} size="small">
            <CloseIcon />
          </IconButton>
        </Stack>
      </DialogTitle>
      <DialogContent>
        <Box
          sx={{
            padding: '20px 0',
            width: '100%',
            display: 'flex',
            flexDirection: 'column',
            gap: '20px',
          }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              alignItems: 'center',
              gap: '12px',
              'svg path': {
                stroke: theme.palette.getContrastText(theme.palette.background.default),
              },
            }}>
            <IconPostCall />
            <Typography fontSize="18px" fontWeight={600}>
              {t('campaignPage.postCall')}
            </Typography>
          </Box>

          <FormProvider {...formMethods}>
            <StaticSettingsBlock
              header={
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  width="100%">
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <IconPhoneIncoming width={16} />
                    <Typography fontSize="16px" fontWeight={600}>
                      {t('campaignPage.callbackRequestCheckbox')}
                    </Typography>
                  </Stack>
                  <FormGroup>
                    <FormControlLabel
                      sx={{
                        marginRight: 0,
                      }}
                      control={
                        <Controller
                          name={'callbackRequestTool.enabled'}
                          control={control}
                          render={({ field }) => {
                            return (
                              <CustomSwitch
                                {...field}
                                checked={Boolean(field.value)}
                                disabled={isReadOnly}
                                onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                  field.onChange(e.target.checked);
                                }}
                              />
                            );
                          }}
                        />
                      }
                      label=""
                    />
                  </FormGroup>
                </Stack>
              }
            />

            <ExpandableSettingsBlock
              id="idle"
              expanded={expandedSettings.idle}
              onChange={(expanded) => setSettingsExpanded('idle', expanded)}
              header={
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  width="100%">
                  <Typography fontSize="16px" fontWeight={600}>
                    {t('adminMainPage.agent.idleState.title')}
                  </Typography>
                  <FormGroup>
                    <FormControlLabel
                      sx={{ marginRight: 0 }}
                      onClick={(event: React.MouseEvent) => event.stopPropagation()}
                      control={
                        <CustomSwitch
                          checked={idleStateDraft.enabled}
                          disabled={isReadOnly}
                          onClick={(event: React.MouseEvent) => event.stopPropagation()}
                          onChange={(e: ChangeEvent<HTMLInputElement>) => {
                            updateIdleStateDraft({ enabled: e.target.checked });
                            setSettingsExpanded('idle', e.target.checked);
                          }}
                        />
                      }
                      label=""
                    />
                  </FormGroup>
                </Stack>
              }>
              <Stack spacing={2}>
                <CustomTextField
                  label={t('adminMainPage.agent.idleState.delayLabel')}
                  type="number"
                  fullWidth
                  disabled={isReadOnly || !idleStateDraft.enabled}
                  inputProps={{
                    min: AGENT_IDLE_STATE_DELAY_MS_MIN,
                    max: AGENT_IDLE_STATE_DELAY_MS_MAX,
                    step: 1,
                  }}
                  value={idleStateDraft.delayMs}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    updateIdleStateDraft({ delayMs: event.target.value })
                  }
                />
                {idleStateErrors.delayMs && (
                  <StyledErrorText>{idleStateErrors.delayMs}</StyledErrorText>
                )}

                <MessageGroupEditor
                  groups={['idle']}
                  value={{ idle: idleStateDraft.messages }}
                  disabled={isReadOnly || !idleStateDraft.enabled}
                  onChange={(value) => updateIdleStateDraft({ messages: value.idle ?? [] })}
                  labels={{
                    helperText: t('adminMainPage.agent.idleState.messagesHelper'),
                    emptyGroupText: t('adminMainPage.agent.idleState.messagesEmpty'),
                    addMessageLabel: t('adminMainPage.agent.idleState.addMessage'),
                    messagePlaceholder: t('adminMainPage.agent.idleState.messagePlaceholder'),
                    groupLabels: { idle: t('adminMainPage.agent.idleState.messagesGroup') },
                    deleteMessageAriaLabel: (_group, index) =>
                      t('adminMainPage.agent.idleState.deleteMessage', { index }),
                  }}
                />
                {idleStateErrors.messages && (
                  <StyledErrorText>{idleStateErrors.messages}</StyledErrorText>
                )}
              </Stack>
            </ExpandableSettingsBlock>

            <ExpandableSettingsBlock
              id="customerIdle"
              expanded={expandedSettings.customerIdle}
              onChange={(expanded) => setSettingsExpanded('customerIdle', expanded)}
              header={
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  width="100%">
                  <Typography fontSize="16px" fontWeight={600}>
                    {t('adminMainPage.agent.idleState.customerTitle')}
                  </Typography>
                </Stack>
              }>
              <Stack spacing={2}>
                <Typography color="text.secondary" fontSize="14px">
                  {t('adminMainPage.agent.idleState.customerHelper')}
                </Typography>

                <Box sx={{ display: 'flex', gap: 2, flexWrap: 'wrap' }}>
                  <Box sx={{ flex: 1, minWidth: 240 }}>
                    <CustomTextField
                      label={t('adminMainPage.agent.idleState.replyTimeoutLabel')}
                      type="number"
                      fullWidth
                      disabled={isReadOnly}
                      inputProps={{
                        min: CUSTOMER_IDLE_STATE_TIMEOUT_SEC_MIN,
                        max: CUSTOMER_IDLE_STATE_TIMEOUT_SEC_MAX,
                        step: 0.1,
                      }}
                      value={idleStateDraft.customerReplyTimeoutSec}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        updateIdleStateDraft({ customerReplyTimeoutSec: event.target.value })
                      }
                    />
                    {idleStateErrors.customerReplyTimeoutSec && (
                      <StyledErrorText>{idleStateErrors.customerReplyTimeoutSec}</StyledErrorText>
                    )}
                  </Box>
                  <Box sx={{ flex: 1, minWidth: 240 }}>
                    <CustomTextField
                      label={t('adminMainPage.agent.idleState.silenceThresholdLabel')}
                      type="number"
                      fullWidth
                      disabled={isReadOnly}
                      inputProps={{
                        min: CUSTOMER_IDLE_STATE_TIMEOUT_SEC_MIN,
                        max: CUSTOMER_IDLE_STATE_TIMEOUT_SEC_MAX,
                        step: 0.1,
                      }}
                      value={idleStateDraft.customerSilenceThresholdSec}
                      onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                        updateIdleStateDraft({ customerSilenceThresholdSec: event.target.value })
                      }
                    />
                    {idleStateErrors.customerSilenceThresholdSec && (
                      <StyledErrorText>
                        {idleStateErrors.customerSilenceThresholdSec}
                      </StyledErrorText>
                    )}
                  </Box>
                </Box>

                <CustomTextField
                  label={t('adminMainPage.agent.idleState.maxAttemptsLabel')}
                  type="number"
                  fullWidth
                  disabled={isReadOnly}
                  inputProps={{
                    min: CUSTOMER_IDLE_STATE_MAX_ATTEMPTS_MIN,
                    max: CUSTOMER_IDLE_STATE_MAX_ATTEMPTS_MAX,
                    step: 1,
                  }}
                  value={idleStateDraft.customerMaxAttempts}
                  onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                    updateIdleStateDraft({ customerMaxAttempts: event.target.value })
                  }
                />
                {idleStateErrors.customerMaxAttempts && (
                  <StyledErrorText>{idleStateErrors.customerMaxAttempts}</StyledErrorText>
                )}

                <MessageGroupEditor
                  groups={['customerSilence']}
                  value={{ customerSilence: idleStateDraft.customerSilenceMessages }}
                  disabled={isReadOnly}
                  onChange={(value) =>
                    updateIdleStateDraft({ customerSilenceMessages: value.customerSilence ?? [] })
                  }
                  labels={{
                    helperText: t('adminMainPage.agent.idleState.customerMessagesHelper'),
                    emptyGroupText: t('adminMainPage.agent.idleState.customerMessagesEmpty'),
                    addMessageLabel: t('adminMainPage.agent.idleState.addCustomerMessage'),
                    messagePlaceholder: t(
                      'adminMainPage.agent.idleState.customerMessagePlaceholder',
                    ),
                    groupLabels: {
                      customerSilence: t('adminMainPage.agent.idleState.customerMessagesGroup'),
                    },
                    deleteMessageAriaLabel: (_group, index) =>
                      t('adminMainPage.agent.idleState.deleteCustomerMessage', { index }),
                  }}
                />

                <MessageGroupEditor
                  groups={['customerEndCall']}
                  value={{ customerEndCall: idleStateDraft.customerEndCallMessages }}
                  disabled={isReadOnly}
                  onChange={(value) =>
                    updateIdleStateDraft({ customerEndCallMessages: value.customerEndCall ?? [] })
                  }
                  labels={{
                    helperText: t('adminMainPage.agent.idleState.customerEndCallMessagesHelper'),
                    emptyGroupText: t('adminMainPage.agent.idleState.customerEndCallMessagesEmpty'),
                    addMessageLabel: t('adminMainPage.agent.idleState.addCustomerEndCallMessage'),
                    messagePlaceholder: t(
                      'adminMainPage.agent.idleState.customerEndCallMessagePlaceholder',
                    ),
                    groupLabels: {
                      customerEndCall: t(
                        'adminMainPage.agent.idleState.customerEndCallMessagesGroup',
                      ),
                    },
                    deleteMessageAriaLabel: (_group, index) =>
                      t('adminMainPage.agent.idleState.deleteCustomerEndCallMessage', { index }),
                  }}
                />
              </Stack>
            </ExpandableSettingsBlock>

            <StaticSettingsBlock
              header={
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  width="100%">
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <IconMicrophone width={16} />
                    <Typography fontSize="16px" fontWeight={600}>
                      {t('campaignPage.voiceMailDetectionCheckbox')}
                    </Typography>
                  </Stack>
                  <FormGroup>
                    <FormControlLabel
                      sx={{
                        marginRight: 0,
                      }}
                      onClick={(event: React.MouseEvent) => event.stopPropagation()}
                      control={
                        <Controller
                          name={'voiceMailDetectionTool.enabled'}
                          control={control}
                          render={({ field }) => (
                            <CustomSwitch
                              {...field}
                              checked={Boolean(field.value)}
                              disabled={isReadOnly}
                              onClick={(event: React.MouseEvent) => event.stopPropagation()}
                              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                field.onChange(e.target.checked);
                              }}
                            />
                          )}
                        />
                      }
                      label=""
                    />
                  </FormGroup>
                </Stack>
              }
            />

            <StaticSettingsBlock
              header={
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  width="100%">
                  <Stack direction="row" alignItems="center" spacing={1}>
                    <IconPhoneOff width={16} />
                    <Typography fontSize="16px" fontWeight={600}>
                      {t('campaignPage.endCallCheckbox')}
                    </Typography>
                  </Stack>
                  <FormGroup>
                    <FormControlLabel
                      sx={{
                        marginRight: 0,
                      }}
                      onClick={(event: React.MouseEvent) => event.stopPropagation()}
                      control={
                        <Controller
                          name={'endCallTool.enabled'}
                          control={control}
                          render={({ field }) => (
                            <CustomSwitch
                              {...field}
                              checked={Boolean(field.value)}
                              disabled={isReadOnly}
                              onClick={(event: React.MouseEvent) => event.stopPropagation()}
                              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                field.onChange(e.target.checked);
                              }}
                            />
                          )}
                        />
                      }
                      label=""
                    />
                  </FormGroup>
                </Stack>
              }
            />

            <ExpandableSettingsBlock
              id="whatsapp"
              expanded={expandedSettings.whatsapp}
              onChange={(expanded) => setSettingsExpanded('whatsapp', expanded)}
              header={
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  width="100%">
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{
                      'svg path': {
                        fill: theme.palette.getContrastText(theme.palette.background.default),
                      },
                    }}>
                    <IconWhatsapp />
                    <Typography fontSize="16px" fontWeight={600}>
                      {t('campaignPage.whatsAppMessageCheckbox')}
                    </Typography>
                  </Stack>
                  <FormGroup>
                    <FormControlLabel
                      sx={{
                        marginRight: 0,
                      }}
                      control={
                        <Controller
                          name={'sendFollowUpMessageTool.enabled'}
                          control={control}
                          render={({ field }) => (
                            <CustomSwitch
                              {...field}
                              checked={Boolean(field.value)}
                              disabled={isReadOnly}
                              onClick={(event: React.MouseEvent) => event.stopPropagation()}
                              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                field.onChange(e.target.checked);
                                setSettingsExpanded('whatsapp', e.target.checked);
                              }}
                            />
                          )}
                        />
                      }
                      label=""
                    />
                  </FormGroup>
                </Stack>
              }>
              <Box>
                <MessageTemplateField<AgentToolsFormValue>
                  fieldName="sendFollowUpMessageTool.messageTemplate"
                  previewValues={templatePreviewValues}
                  disabled={isReadOnly}
                  required={Boolean(formData.sendFollowUpMessageTool?.enabled)}
                />
              </Box>
            </ExpandableSettingsBlock>

            <StaticSettingsBlock
              header={
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  width="100%">
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{
                      'svg path': {
                        fill: theme.palette.getContrastText(theme.palette.background.default),
                      },
                    }}>
                    <IconWhatsapp />
                    <Typography fontSize="16px" fontWeight={600}>
                      {t('campaignPage.generateWhatsappMessageCheckbox')}
                    </Typography>
                  </Stack>
                  <FormGroup>
                    <FormControlLabel
                      sx={{
                        marginRight: 0,
                      }}
                      control={
                        <Controller
                          name={'generateWhatsappMessageTool.enabled'}
                          control={control}
                          render={({ field }) => (
                            <CustomSwitch
                              {...field}
                              checked={Boolean(field.value)}
                              disabled={isReadOnly}
                              onClick={(event: React.MouseEvent) => event.stopPropagation()}
                              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                field.onChange(e.target.checked);
                              }}
                            />
                          )}
                        />
                      }
                      label=""
                    />
                  </FormGroup>
                </Stack>
              }
            />

            <ExpandableSettingsBlock
              id="transfer"
              expanded={expandedSettings.transfer}
              onChange={(expanded) => setSettingsExpanded('transfer', expanded)}
              header={
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  width="100%">
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{
                      'svg path': {
                        stroke: theme.palette.getContrastText(theme.palette.background.default),
                      },
                    }}>
                    <IconPhoneCall width={16} />
                    <Typography fontSize="16px" fontWeight={600}>
                      {t('campaignPage.transferCallCheckbox')}
                    </Typography>
                  </Stack>
                  <FormGroup>
                    <FormControlLabel
                      sx={{
                        marginRight: 0,
                      }}
                      control={
                        <Controller
                          name={'TransferTool.enabled'}
                          control={control}
                          render={({ field }) => (
                            <CustomSwitch
                              {...field}
                              checked={Boolean(field.value)}
                              disabled={isReadOnly}
                              onClick={(event: React.MouseEvent) => event.stopPropagation()}
                              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                field.onChange(e.target.checked);
                                setSettingsExpanded('transfer', e.target.checked);
                                if (!e.target.checked) {
                                  setValue('TransferTool.phoneNumbers', []);
                                }
                              }}
                            />
                          )}
                        />
                      }
                      label=""
                    />
                  </FormGroup>
                </Stack>
              }>
              <Box sx={{ mt: 2 }}>
                <Controller
                  name={'TransferTool.phoneNumbers'}
                  control={control}
                  render={({ field }) => (
                    <PhoneNumbersField
                      isTransfer
                      phoneNumbers={field.value || []}
                      onChange={(phoneNumbers) => {
                        field.onChange(phoneNumbers);
                        if (phoneNumbers.length > 0) {
                          setValue('TransferTool.enabled', true);
                        } else {
                          setValue('TransferTool.enabled', false);
                        }
                      }}
                      disabled={isReadOnly}
                    />
                  )}
                />
              </Box>
            </ExpandableSettingsBlock>

            <StaticSettingsBlock
              header={
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  width="100%">
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{
                      'svg path': {
                        stroke: theme.palette.getContrastText(theme.palette.background.default),
                      },
                    }}>
                    <IconPhoneCall width={16} />
                    <Typography fontSize="16px" fontWeight={600}>
                      {t('campaignPage.callHandoffSignalCheckbox')}
                    </Typography>
                  </Stack>
                  <FormGroup>
                    <FormControlLabel
                      sx={{ marginRight: 0 }}
                      onClick={(event: React.MouseEvent) => event.stopPropagation()}
                      control={
                        <Controller
                          name={'callHandoffSignalTool.enabled'}
                          control={control}
                          render={({ field }) => (
                            <CustomSwitch
                              {...field}
                              checked={Boolean(field.value)}
                              disabled={isReadOnly}
                              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                field.onChange(e.target.checked);
                              }}
                            />
                          )}
                        />
                      }
                      label=""
                    />
                  </FormGroup>
                </Stack>
              }
            />

            <ExpandableSettingsBlock
              id="gender"
              expanded={expandedSettings.gender}
              onChange={(expanded) => setSettingsExpanded('gender', expanded)}
              header={
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  width="100%">
                  <Stack
                    direction="row"
                    alignItems="center"
                    spacing={1}
                    sx={{
                      'svg path': {
                        stroke: theme.palette.getContrastText(theme.palette.background.default),
                      },
                    }}>
                    <IconMicrophone width={16} />
                    <Typography fontSize="16px" fontWeight={600}>
                      {t('campaignPage.genderDetectionCheckbox')}
                    </Typography>
                  </Stack>
                  <FormGroup>
                    <FormControlLabel
                      sx={{ marginRight: 0 }}
                      control={
                        <Controller
                          name={'genderDetectionTool.enabled'}
                          control={control}
                          render={({ field }) => (
                            <CustomSwitch
                              {...field}
                              checked={Boolean(field.value)}
                              disabled={isReadOnly}
                              onClick={(event: React.MouseEvent) => event.stopPropagation()}
                              onChange={(e: ChangeEvent<HTMLInputElement>) => {
                                field.onChange(e.target.checked);
                                setSettingsExpanded('gender', e.target.checked);
                              }}
                            />
                          )}
                        />
                      }
                      label=""
                    />
                  </FormGroup>
                </Stack>
              }>
              <Controller
                name={'genderDetectionTool.enabled'}
                control={control}
                render={({ field: enabledField }) => (
                  <Box sx={{ mt: 2, display: 'flex', flexDirection: 'column', gap: 2 }}>
                    <Box sx={{ display: 'flex', gap: 2 }}>
                      <Controller
                        name={'genderDetectionTool.confidenceThreshold'}
                        control={control}
                        render={({ field }) => (
                          <CustomTextField
                            {...field}
                            label={t('campaignPage.genderDetection.confidenceThreshold')}
                            type="number"
                            inputProps={{ step: 0.01, min: 0, max: 1 }}
                            fullWidth
                            disabled={isReadOnly || !enabledField.value}
                            value={field.value ?? ''}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              field.onChange(
                                e.target.value === '' ? undefined : parseFloat(e.target.value),
                              );
                            }}
                          />
                        )}
                      />
                      <Controller
                        name={'genderDetectionTool.durationThreshold'}
                        control={control}
                        render={({ field }) => (
                          <CustomTextField
                            {...field}
                            label={t('campaignPage.genderDetection.durationThreshold')}
                            type="number"
                            inputProps={{ step: 1, min: 0 }}
                            fullWidth
                            disabled={isReadOnly || !enabledField.value}
                            value={field.value ?? ''}
                            onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                              field.onChange(
                                e.target.value === '' ? undefined : parseInt(e.target.value, 10),
                              );
                            }}
                          />
                        )}
                      />
                    </Box>
                    <Controller
                      name={'genderDetectionTool.behaviorTemplate'}
                      control={control}
                      render={({ field }) => (
                        <CustomTextField
                          {...field}
                          label={t('campaignPage.genderDetection.behaviorTemplate')}
                          fullWidth
                          multiline
                          rows={3}
                          disabled={isReadOnly || !enabledField.value}
                          value={field.value ?? ''}
                        />
                      )}
                    />
                  </Box>
                )}
              />
            </ExpandableSettingsBlock>
          </FormProvider>

          <AgentMcpToolsSection
            agentId={agent.id || ''}
            agents={agents}
            tools={allAgentTools}
            refetch={() => {
              refetchAgentTools();
              refetch();
            }}
            disabled={isReadOnly}
          />
        </Box>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>{t('adminMainPage.operation.cancel')}</Button>
        {!isReadOnly && (
          <Button onClick={handleSave} variant="contained" color="primary">
            {t('adminMainPage.operation.save')}
          </Button>
        )}
      </DialogActions>
    </Dialog>
  );
};

export default AgentAdvancedSettingsModal;
